import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import {
  Truck, Search, RefreshCw, PackageCheck, PackageX, AlertTriangle, RotateCcw, Undo2, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { formatIST } from '../lib/formatDate';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import api from '../lib/api';

interface UnicommerceProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface Summary {
  connected: boolean;
  synced_at: string | null;
  active_sku: number;
  inactive_sku: number;
  total_sku: number;
  out_of_stock: number;
  total_inventory_skus: number;
  dispatch_count: number;
  rto_count: number;
  returns_initiated_count: number;
  courier_counts: { name: string; count: number }[];
  daily_trend: { date: string; dispatch: number; rto: number; returns_initiated: number }[];
}

interface InventoryItem { itemTypeSKU: string; inventory: number; openSale: number; inventoryBlocked: number; }

const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const fmtDate = (s: string) => { try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); } catch { return s; } };

const BLUE = '#1d4ed8';
const SKY = '#0ea5e9';
const PALE_SKY = '#93c5fd';

type Entity = 'item_types' | 'shipments' | 'returns';

interface DrilldownState { title: string; entity: Entity; filters: Record<string, string>; }

const DRILLDOWN_COLUMNS: Record<Entity, { key: string; label: string }[]> = {
  item_types: [
    { key: 'skuCode', label: 'SKU' }, { key: 'name', label: 'Name' },
    { key: 'categoryName', label: 'Category' }, { key: 'brand', label: 'Brand' },
    { key: 'enabled', label: 'Status' },
  ],
  shipments: [
    { key: 'code', label: 'Shipment Code' }, { key: 'saleOrderCode', label: 'Sale Order' },
    { key: 'status', label: 'Status' }, { key: 'shippingCourier', label: 'Courier' },
    { key: 'city', label: 'City' }, { key: 'date', label: 'Dispatched' },
  ],
  returns: [
    { key: 'code', label: 'Return Code' }, { key: 'returnType', label: 'Type' },
    { key: 'date', label: 'Date' }, { key: 'updated', label: 'Last Updated' },
  ],
};

function KpiCard({ label, value, icon: Icon, accent, note, onClick }: { label: string; value: number; icon: any; accent: string; note?: string; onClick: () => void }) {
  return (
    <button
      data-testid={`warehouse-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onClick}
      className="text-left bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm space-y-3 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 group"
    >
      <div className="flex items-center justify-between">
        <span className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
          <Icon className="h-4.5 w-4.5" />
        </span>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400 group-hover:translate-x-0.5 transition-all" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-800 font-mono tracking-tight">{value.toLocaleString('en-IN')}</p>
        <p className="text-[10.5px] font-semibold text-slate-500 mt-0.5">{label}</p>
        {note && <p className="text-[9px] text-slate-400 font-mono mt-1">{note}</p>}
      </div>
    </button>
  );
}

export default function Unicommerce({ onHover }: UnicommerceProps) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [facilities, setFacilities] = useState<string[]>([]);
  const [inventoryHealth, setInventoryHealth] = useState({ out: 0, low: 0, healthy: 0, total: 0 });
  const [invQuery, setInvQuery] = useState('');
  const [activeFacility, setActiveFacility] = useState<string | null>(null);
  const [stockFilter, setStockFilter] = useState<'all' | 'out' | 'low' | 'healthy'>('all');

  const [drilldown, setDrilldown] = useState<DrilldownState | null>(null);
  const [drilldownRows, setDrilldownRows] = useState<any[]>([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownPage, setDrilldownPage] = useState(1);
  const [drilldownTotalPages, setDrilldownTotalPages] = useState(1);
  const [drilldownTotalCount, setDrilldownTotalCount] = useState(0);

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 89); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, ninetyDaysAgo, today));

  const fetchSummary = () => {
    const start_date = toISO(range.from);
    const end_date = toISO(range.to);
    setLoading(true);
    api.get('/warehouse/unicommerce/summary', { params: { start_date, end_date } })
      .then((res) => setSummary(res.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  };

  useEffect(fetchSummary, [range]);

  // Inventory is a live snapshot (not date-range scoped) — facility, search text and the stock
  // health filter are all applied server-side so we never ship the full SKU list to the browser.
  useEffect(() => {
    const timer = setTimeout(() => {
      api.get('/warehouse/unicommerce/inventory', { params: { facility: activeFacility || '', search: invQuery, stock_filter: stockFilter, page: 1, limit: 50 } })
        .then((res) => {
          setInventory(res.data.items || []);
          setFacilities(res.data.facilities || []);
          setInventoryHealth(res.data.inventory_health || { out: 0, low: 0, healthy: 0, total: 0 });
          if (!activeFacility && res.data.active_facility) setActiveFacility(res.data.active_facility);
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [activeFacility, invQuery, stockFilter]);

  useEffect(() => {
    if (!drilldown) return;
    setDrilldownLoading(true);
    api.get('/warehouse/unicommerce/records', {
      params: { entity: drilldown.entity, start_date: toISO(range.from), end_date: toISO(range.to), page: drilldownPage, limit: 20, ...drilldown.filters },
    })
      .then((res) => {
        setDrilldownRows(res.data.items || []);
        setDrilldownTotalPages(res.data.total_pages || 1);
        setDrilldownTotalCount(res.data.total_count || 0);
      })
      .catch(() => setDrilldownRows([]))
      .finally(() => setDrilldownLoading(false));
  }, [drilldown, drilldownPage, range]);

  const openDrilldown = (title: string, entity: Entity, filters: Record<string, string> = {}) => {
    setDrilldownPage(1);
    setDrilldown({ title, entity, filters });
  };

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      await api.post('/warehouse/unicommerce/sync');
      // fire-and-forget on the backend — a full pull takes minutes, poll once shortly after
      setTimeout(fetchSummary, 8000);
    } catch (e) {
      // surfaced via the connection badge staying stale
    } finally {
      setSyncing(false);
    }
  };

  const dailyTrend = useMemo(() => (summary?.daily_trend || []).map((d) => ({ ...d, date: fmtDate(d.date) })), [summary]);
  const skuHealthData = useMemo(() => summary ? [
    { name: 'Active', value: summary.active_sku },
    { name: 'Inactive', value: summary.inactive_sku },
  ] : [], [summary]);

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="warehouse-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">
            Fulfillment & Logistics Hub
          </span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            Warehouse — Unicommerce
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">
            Live SKU catalog, inventory, dispatch and returns synced directly from Unicommerce every 2 hours.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="warehouse" />
          <button
            data-testid="warehouse-manual-sync-button"
            onClick={handleManualSync}
            disabled={syncing}
            className="px-4 py-2.5 rounded-xl flex items-center gap-2 text-xs font-semibold border bg-white text-blue-700 border-blue-100 hover:border-blue-300 transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            <span>{syncing ? 'Syncing…' : 'Manual Sync'}</span>
          </button>
          <div
            data-testid="warehouse-connection-badge"
            className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${
              summary?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'
            }`}
          >
            <Truck className="h-4.5 w-4.5" />
            <span>{summary?.connected ? `Synced ${summary.synced_at ? formatIST(summary.synced_at) : ''}` : 'Not Connected'}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div data-testid="warehouse-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">
          Loading live Unicommerce data…
        </div>
      ) : !summary?.connected ? (
        <div data-testid="warehouse-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">
          Unicommerce isn't connected yet. Ask your admin to save credentials in Admin Console → Integrations.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="warehouse-kpi-grid">
            <KpiCard label="Active SKU" value={summary.active_sku} icon={PackageCheck} accent={BLUE} note="As of last sync" onClick={() => openDrilldown('Active SKUs', 'item_types', { enabled: 'true' })} />
            <KpiCard label="Inactive SKU" value={summary.inactive_sku} icon={PackageX} accent="#64748b" note="As of last sync" onClick={() => openDrilldown('Inactive SKUs', 'item_types', { enabled: 'false' })} />
            <KpiCard label="Out of Stock" value={summary.out_of_stock} icon={AlertTriangle} accent="#e11d48" note="As of last sync" onClick={() => { setStockFilter('out'); const el = document.querySelector('[data-testid="warehouse-inventory-section"]'); el?.scrollIntoView({ behavior: 'smooth' }); }} />
            <KpiCard label="Number of Dispatch" value={summary.dispatch_count} icon={Truck} accent={SKY} note="Selected window" onClick={() => openDrilldown('Dispatched Shipments', 'shipments', {})} />
            <KpiCard label="RTO" value={summary.rto_count} icon={RotateCcw} accent="#d97706" note="Selected window" onClick={() => openDrilldown('RTO — Return to Origin', 'returns', { return_type: 'RTO' })} />
            <KpiCard label="Returns Initiated" value={summary.returns_initiated_count} icon={Undo2} accent={PALE_SKY} note="Selected window" onClick={() => openDrilldown('Returns Initiated', 'returns', { return_type: 'CIR' })} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-3">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Dispatch, RTO & Returns Initiated — Daily Trend</span>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyTrend} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px', fontSize: '11px' }} />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                    <Line type="monotone" dataKey="dispatch" stroke={BLUE} strokeWidth={2.5} dot={false} name="Dispatch" />
                    <Line type="monotone" dataKey="rto" stroke={SKY} strokeWidth={2} dot={false} name="RTO" strokeDasharray="4 3" />
                    <Line type="monotone" dataKey="returns_initiated" stroke={PALE_SKY} strokeWidth={2} dot={false} name="Returns Initiated" strokeDasharray="2 2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-3">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">SKU Health</span>
              <div className="h-52 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={skuHealthData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={42} outerRadius={72} paddingAngle={3}>
                      <Cell fill={BLUE} />
                      <Cell fill="#cbd5e1" />
                    </Pie>
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px', fontSize: '11px' }} />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-slate-400 font-mono text-center">{summary.total_sku.toLocaleString('en-IN')} total SKUs in catalog</p>
            </div>
          </div>

          <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-3">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Courier Performance — Dispatches in Selected Window</span>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.courier_counts} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px', fontSize: '11px' }} />
                  <Bar dataKey="count" name="Dispatches" radius={[6, 6, 0, 0]}>
                    {(summary.courier_counts || []).map((_, i) => <Cell key={i} fill={i % 2 === 0 ? BLUE : SKY} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="warehouse-inventory-section">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Inventory Snapshot</span>
              <div className="flex gap-2">
                {facilities.map((fc) => (
                  <button
                    key={fc}
                    data-testid={`warehouse-facility-${fc}`}
                    onClick={() => setActiveFacility(fc)}
                    className={`px-3 py-1.5 rounded-full text-[10.5px] font-semibold border transition-colors ${
                      activeFacility === fc ? 'bg-blue-700 text-white border-blue-700' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {fc}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'all', label: `All (${inventoryHealth.total})` },
                { key: 'out', label: `Out of Stock (${inventoryHealth.out})` },
                { key: 'low', label: `Low Stock ≤5 (${inventoryHealth.low})` },
                { key: 'healthy', label: `Healthy (${inventoryHealth.healthy})` },
              ] as const).map((f) => (
                <button
                  key={f.key}
                  data-testid={`warehouse-stock-filter-${f.key}`}
                  onClick={() => setStockFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-[10.5px] font-bold border transition-colors ${
                    stockFilter === f.key ? 'bg-blue-700 text-white border-blue-700' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                data-testid="warehouse-inventory-search"
                value={invQuery}
                onChange={(e) => setInvQuery(e.target.value)}
                placeholder="Search SKU…"
                className="w-full bg-slate-50 border border-slate-200 pl-8 pr-3 py-2 text-xs rounded-xl focus:outline-none focus:border-blue-400 text-slate-700"
              />
            </div>
            <div className="overflow-auto max-h-[400px] border border-slate-100 rounded-2xl">
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-slate-100 text-[10.5px] text-slate-400 font-mono font-bold">
                    <th className="py-3 px-4">SKU</th>
                    <th className="py-3 px-4">Inventory</th>
                    <th className="py-3 px-4">Open Sale</th>
                    <th className="py-3 px-4">Blocked</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {inventory.map((item, i) => (
                    <tr key={i} className="hover:bg-blue-50/40">
                      <td className="py-2.5 px-4 font-semibold">{item.itemTypeSKU}</td>
                      <td className="py-2.5 px-4">{item.inventory}</td>
                      <td className="py-2.5 px-4">{item.openSale}</td>
                      <td className="py-2.5 px-4">{item.inventoryBlocked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {drilldown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" data-testid="warehouse-drilldown-modal" onClick={() => setDrilldown(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h3 className="font-display font-semibold text-slate-800 text-sm">{drilldown.title}</h3>
                <p className="text-[10px] text-slate-400 font-mono mt-0.5">{drilldownTotalCount.toLocaleString('en-IN')} records</p>
              </div>
              <button data-testid="warehouse-drilldown-close" onClick={() => setDrilldown(null)} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100 text-slate-500">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-auto flex-1">
              {drilldownLoading ? (
                <p className="text-xs text-slate-400 font-mono p-6 text-center">Loading…</p>
              ) : drilldownRows.length === 0 ? (
                <p className="text-xs text-slate-400 font-mono p-6 text-center">No records found for this window.</p>
              ) : (
                <table className="w-full text-left text-xs text-slate-600">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b border-slate-100 text-[10.5px] text-slate-400 font-mono font-bold">
                      {DRILLDOWN_COLUMNS[drilldown.entity].map((c) => <th key={c.key} className="py-3 px-4">{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {drilldownRows.map((row, i) => (
                      <tr key={i} className="hover:bg-blue-50/40">
                        {DRILLDOWN_COLUMNS[drilldown.entity].map((c) => (
                          <td key={c.key} className="py-2.5 px-4">
                            {c.key === 'enabled' ? (
                              <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full border ${row[c.key] ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>{row[c.key] ? 'Active' : 'Inactive'}</span>
                            ) : (row[c.key] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
              <button
                data-testid="warehouse-drilldown-prev"
                disabled={drilldownPage <= 1}
                onClick={() => setDrilldownPage((p) => Math.max(1, p - 1))}
                className="flex items-center gap-1 text-xs font-semibold text-slate-500 disabled:opacity-30 hover:text-blue-700"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <span className="text-[10px] font-mono text-slate-400">Page {drilldownPage} of {drilldownTotalPages}</span>
              <button
                data-testid="warehouse-drilldown-next"
                disabled={drilldownPage >= drilldownTotalPages}
                onClick={() => setDrilldownPage((p) => Math.min(drilldownTotalPages, p + 1))}
                className="flex items-center gap-1 text-xs font-semibold text-slate-500 disabled:opacity-30 hover:text-blue-700"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
